// Container for the Usage & limits table: plugs the customer mutation into the
// presentational UsageTable. Saving a cap sends exactly what the old inputs
// sent (`{ <key>: N }` through handleUpdateRestrictions), so RisikaAPI and the
// Restore Defaults dialog are untouched.

import * as React from 'react'
import { useParams } from 'react-router'
import { useUpdateCustomer } from 'services/queries/use-update-customer'
import { CustomerRestrictionsResponse } from 'services/cm-api/get-customer'
import { Stack } from '@mui/material'
import { handleUpdateRestrictions } from '../restrictions.controller'
import {
  NORDIC_START_DATE_ELEMENT,
  nordicContractYear,
} from '../nordic-contract-year'
import NordicContractYearPanel from '../widgets/nordic-contract-year-panel'
import UsageTable from './usage-table'

type Props = {
  data: CustomerRestrictionsResponse
  usersLength: number
  search: string
}

function UsageAndLimits({ data, usersLength, search }: Props) {
  const { id: selectedCustomer } = useParams()
  const { mutate: updateCustomer, isLoading } = useUpdateCustomer()

  const onSaveCap = React.useCallback(
    (key: string, value: number) => {
      if (!selectedCustomer) return
      handleUpdateRestrictions({
        selectedCustomer,
        updateCustomer,
        newValue: value,
        element: { key },
      })
    },
    [selectedCustomer, updateCustomer],
  )

  // ENG2-2461: the Nordic lookup contract year, for the 2026 packages, once
  // RisikaAPI reports it.
  const contractYear = nordicContractYear(
    data.subscription_plan,
    data.counters,
    data.rights?.global as Record<string, unknown> | undefined,
  )
  const onChangeStartDate = React.useCallback(
    (isoDate: string) => {
      if (!selectedCustomer) return
      handleUpdateRestrictions({
        selectedCustomer,
        updateCustomer,
        newValue: isoDate,
        element: NORDIC_START_DATE_ELEMENT,
      })
    },
    [selectedCustomer, updateCustomer],
  )

  // The table works on numeric counters only; the period dates go in through
  // nordicPeriod.
  const {
    nordic_lookups_current_period_start: _start,
    nordic_lookups_current_period_end: _end,
    ...numericCounters
  } = data.counters

  const table = (
    <UsageTable
      plan={data.subscription_plan}
      usersLength={usersLength}
      caps={{
        ...data.caps,
        max_users: data.max_users,
        search_exports_limit: data.rights?.search_exports_limit,
      }}
      counters={numericCounters}
      search={search}
      isSaving={isLoading}
      onSaveCap={onSaveCap}
      nordicPeriod={contractYear.show ? contractYear.period : null}
    />
  )

  if (!contractYear.show) return table

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {table}
      <NordicContractYearPanel
        startDate={contractYear.startDate}
        period={contractYear.period}
        isSaving={isLoading}
        onChangeStartDate={onChangeStartDate}
      />
    </Stack>
  )
}

export default UsageAndLimits
